"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.setScenarioOverrideRequestSchema = exports.saveScenarioCostRequestSchema = exports.setScenarioCellsRequestSchema = exports.saveScenarioResourceRequestSchema = exports.saveScenarioLineRequestSchema = exports.updateScenarioRequestSchema = exports.createScenarioRequestSchema = exports.dealScenarioDetailSchema = exports.dealScenarioSchema = exports.scenarioFiguresSchema = exports.scenarioCostSchema = exports.scenarioCellSchema = exports.scenarioResourceSchema = exports.scenarioLineSchema = exports.SCENARIO_PERIOD_UNITS = exports.SCENARIO_STATUSES = exports.SCENARIO_KINDS = void 0;
exports.addMonthsWire = addMonthsWire;
exports.scenarioPeriods = scenarioPeriods;
exports.scenarioMonths = scenarioMonths;
exports.scenarioFigures = scenarioFigures;
exports.mondaysIn = mondaysIn;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
const contract_1 = require("./contract");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D9 · a deal's scenarios. `0231`, DM-4, DS-1…DS-6. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The shapes, and the arithmetic in ONE place: the list's revenue / cost / margin columns, the
 * editor's tiles, the recurring schedule with its indexation and overrides, and the copy into the
 * plan all use `scenarioFigures` and `scenarioPeriods` below — so the server's list and the screen's
 * editor cannot disagree about one scenario.
 */
const money = zod_1.z.string();
const hours = zod_1.z.string();
const wireDate = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);
const moneyInput = zod_1.z.union([zod_1.z.number(), zod_1.z.string()]).transform((value, ctx) => {
    const n = typeof value === 'number' ? value : Number(value.replace(/\s/gu, '').replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) {
        ctx.addIssue({ code: 'custom', message: 'not an amount' });
        return zod_1.z.NEVER;
    }
    return Math.round(n * 100) / 100;
});
exports.SCENARIO_KINDS = ['one_off', 'recurring'];
exports.SCENARIO_STATUSES = ['draft', 'sent', 'won', 'dropped'];
exports.SCENARIO_PERIOD_UNITS = ['month', 'quarter', 'year'];
exports.scenarioLineSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    type: zod_1.z.enum(contract_1.DEAL_LINE_ITEM_TYPES),
    /** Per period on a recurring scenario. */
    amount: money,
    startsOn: wireDate.nullable(),
    endsOn: wireDate.nullable(),
    sortOrder: zod_1.z.number().int(),
    version: zod_1.z.number().int(),
});
exports.scenarioResourceSchema = zod_1.z.object({
    id: zod_1.z.string(),
    profileId: zod_1.z.string().nullable(),
    name: zod_1.z.string(),
    seniority: zod_1.z.enum(contract_1.SENIORITIES).nullable(),
    headcount: zod_1.z.number().int(),
    costRate: money,
    color: zod_1.z.string().nullable(),
    sortOrder: zod_1.z.number().int(),
    version: zod_1.z.number().int(),
});
exports.scenarioCellSchema = zod_1.z.object({
    lineId: zod_1.z.string(),
    resourceId: zod_1.z.string(),
    /** A month's first day, or null for a recurring scenario's one-period template. */
    period: wireDate.nullable(),
    /** Per head. */
    hours,
});
exports.scenarioCostSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    amount: money,
    incurredOn: wireDate.nullable(),
    version: zod_1.z.number().int(),
});
/** The figures the list's row and the editor's tiles show. */
exports.scenarioFiguresSchema = zod_1.z.object({
    revenue: money,
    labour: money,
    external: money,
    margin: money,
    /** Margin ÷ revenue, or null without revenue. */
    marginPct: zod_1.z.number().nullable(),
    hours,
    /** First month (one-off) or first period's start, and the last. */
    from: wireDate.nullable(),
    to: wireDate.nullable(),
    /** Months on a one-off scenario, periods on a recurring one. */
    span: zod_1.z.number().int(),
});
exports.dealScenarioSchema = zod_1.z.object({
    id: zod_1.z.string(),
    identifier: (0, identifier_1.identifierSchema)('scenario'),
    name: zod_1.z.string(),
    kind: zod_1.z.enum(exports.SCENARIO_KINDS),
    status: zod_1.z.enum(exports.SCENARIO_STATUSES),
    isWinner: zod_1.z.boolean(),
    startsOn: wireDate,
    periodUnit: zod_1.z.enum(exports.SCENARIO_PERIOD_UNITS),
    periodStep: zod_1.z.number().int(),
    periodCount: zod_1.z.number().int(),
    indexPct: zod_1.z.string(),
    createdByName: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
    /** With options counted. */
    figures: exports.scenarioFiguresSchema,
    version: zod_1.z.number().int(),
});
exports.dealScenarioDetailSchema = exports.dealScenarioSchema.extend({
    lines: zod_1.z.array(exports.scenarioLineSchema),
    resources: zod_1.z.array(exports.scenarioResourceSchema),
    cells: zod_1.z.array(exports.scenarioCellSchema),
    costs: zod_1.z.array(exports.scenarioCostSchema),
    /** Period index → typed revenue (`07g`'s *kézi*). */
    overrides: zod_1.z.record(zod_1.z.string(), money),
    currencyCode: zod_1.z.string(),
    canManage: zod_1.z.boolean(),
});
/* ── requests ──────────────────────────────────────────────────────────────────────────────── */
exports.createScenarioRequestSchema = zod_1.z.strictObject({
    name: zod_1.z.string().trim().min(1).max(120),
    kind: zod_1.z.enum(exports.SCENARIO_KINDS),
    startsOn: wireDate,
    /** Copy everything from this scenario of the same deal (the mockup's *duplikálás*). */
    copyFromId: zod_1.z.uuid().optional(),
});
exports.updateScenarioRequestSchema = zod_1.z.strictObject({
    version: zod_1.z.number().int().positive(),
    name: zod_1.z.string().trim().min(1).max(120).optional(),
    kind: zod_1.z.enum(exports.SCENARIO_KINDS).optional(),
    /** Draft or sent; won and dropped are set by marking a winner. */
    status: zod_1.z.enum(['draft', 'sent']).optional(),
    startsOn: wireDate.optional(),
    periodUnit: zod_1.z.enum(exports.SCENARIO_PERIOD_UNITS).optional(),
    periodStep: zod_1.z.number().int().min(1).max(12).optional(),
    periodCount: zod_1.z.number().int().min(1).max(120).optional(),
    indexPct: zod_1.z.number().min(-50).max(100).optional(),
});
exports.saveScenarioLineRequestSchema = zod_1.z.strictObject({
    name: zod_1.z.string().trim().min(1).max(200),
    type: zod_1.z.enum(contract_1.DEAL_LINE_ITEM_TYPES),
    amount: moneyInput,
    startsOn: wireDate.nullable(),
    endsOn: wireDate.nullable(),
    version: zod_1.z.number().int().positive().optional(),
});
exports.saveScenarioResourceRequestSchema = zod_1.z.strictObject({
    /** From a placeholder profile (role, seniority, cost rate copied) — or a free name and rate. */
    profileId: zod_1.z.uuid().nullable().optional(),
    name: zod_1.z.string().trim().min(1).max(120).optional(),
    seniority: zod_1.z.enum(contract_1.SENIORITIES).nullable().optional(),
    headcount: zod_1.z.number().int().min(1).max(50).optional(),
    costRate: moneyInput.optional(),
    version: zod_1.z.number().int().positive().optional(),
});
exports.setScenarioCellsRequestSchema = zod_1.z.strictObject({
    cells: zod_1.z
        .array(zod_1.z.strictObject({
        lineId: zod_1.z.uuid(),
        resourceId: zod_1.z.uuid(),
        period: wireDate.nullable(),
        /** 0 clears the cell. */
        hours: zod_1.z.number().min(0).max(2000),
    }))
        .min(1)
        .max(500),
});
exports.saveScenarioCostRequestSchema = zod_1.z.strictObject({
    name: zod_1.z.string().trim().min(1).max(160),
    amount: moneyInput,
    incurredOn: wireDate.nullable(),
    version: zod_1.z.number().int().positive().optional(),
});
exports.setScenarioOverrideRequestSchema = zod_1.z.strictObject({
    /** Null removes the override, back to the indexed figure. */
    revenue: moneyInput.nullable(),
});
/* ── the arithmetic, once ──────────────────────────────────────────────────────────────────── */
/** `YYYY-MM-01` plus `n` months. */
function addMonthsWire(month, n) {
    const y = Number(month.slice(0, 4));
    const m = Number(month.slice(5, 7)) - 1 + n;
    const year = y + Math.floor(m / 12);
    const mm = ((m % 12) + 12) % 12;
    return `${String(year)}-${String(mm + 1).padStart(2, '0')}-01`;
}
const firstOf = (date) => `${date.slice(0, 7)}-01`;
const monthsPerPeriod = (input) => input.periodUnit === 'year'
    ? 12
    : input.periodUnit === 'quarter'
        ? 3
        : Math.max(1, input.periodStep);
/**
 * The lines' revenue and the cells' hours and labour. `which`: `'template'` is a recurring
 * scenario's one-period cells (period null); `'all'` is every cell of a one-off scenario.
 */
function tally(input, which) {
    const counted = new Set(input.lines
        .filter((line) => input.withOptions || line.type !== 'option')
        .map((line) => line.id));
    const resources = new Map(input.resources.map((r) => [r.id, r]));
    let hoursSum = 0;
    let labour = 0;
    for (const cell of input.cells) {
        if (!counted.has(cell.lineId))
            continue;
        if (which === 'template' && cell.period !== null)
            continue;
        const resource = resources.get(cell.resourceId);
        if (resource === undefined)
            continue;
        const h = cell.hours * resource.headcount;
        hoursSum += h;
        labour += h * resource.costRate;
    }
    const revenue = input.lines
        .filter((line) => counted.has(line.id))
        .reduce((sum, line) => sum + line.amount, 0);
    return { revenue, hours: hoursSum, labour };
}
/**
 * DS-2 · a recurring scenario's schedule (`07g`): `periodCount` periods from `startsOn`, each the
 * template's revenue × `(1 + index%)^year`, unless a period's revenue was typed by hand. Labour and
 * hours are the template's in every period.
 */
function scenarioPeriods(input) {
    const base = tally(input, 'template');
    const step = monthsPerPeriod(input);
    const perYear = Math.max(1, Math.round(12 / step));
    const start = firstOf(input.startsOn);
    const out = [];
    for (let index = 0; index < Math.max(1, input.periodCount); index += 1) {
        const startsOn = addMonthsWire(start, index * step);
        const next = addMonthsWire(start, (index + 1) * step);
        const end = new Date(`${next}T00:00:00Z`);
        end.setUTCDate(end.getUTCDate() - 1);
        const multiplier = (1 + input.indexPct / 100) ** Math.floor(index / perYear);
        const indexed = Math.round(base.revenue * multiplier * 100) / 100;
        const typed = input.overrides[String(index)];
        out.push({
            index,
            startsOn,
            endsOn: end.toISOString().slice(0, 10),
            revenue: typed ?? indexed,
            indexed,
            overridden: typed !== undefined,
            labour: base.labour,
            hours: base.hours,
            multiplier,
        });
    }
    return out;
}
/** The months a one-off scenario spans: its lines' dates and every month a cell sits in. */
function scenarioMonths(input) {
    const days = [];
    for (const line of input.lines) {
        if (line.startsOn !== null)
            days.push(firstOf(line.startsOn));
        if (line.endsOn !== null)
            days.push(firstOf(line.endsOn));
    }
    for (const cell of input.cells)
        if (cell.period !== null)
            days.push(cell.period);
    days.sort();
    const from = days[0] ?? firstOf(input.startsOn);
    const to = days.at(-1) ?? from;
    const out = [];
    for (let m = from; m <= to && out.length < 120; m = addMonthsWire(m, 1))
        out.push(m);
    /* The mockup always shows at least three months, so a fresh scenario has somewhere to type. */
    while (out.length < 3)
        out.push(addMonthsWire(out.at(-1) ?? from, 1));
    return out;
}
/** DS-1 · revenue, labour, external, margin and hours — the list row and the editor's tiles. */
function scenarioFigures(input) {
    const external = input.costs.reduce((sum, cost) => sum + cost.amount, 0);
    if (input.kind === 'recurring') {
        const periods = scenarioPeriods(input);
        const revenue = periods.reduce((sum, p) => sum + p.revenue, 0);
        const labour = periods.reduce((sum, p) => sum + p.labour, 0);
        const hoursSum = periods.reduce((sum, p) => sum + p.hours, 0);
        const margin = revenue - labour - external;
        return {
            revenue,
            labour,
            external,
            margin,
            marginPct: revenue > 0 ? margin / revenue : null,
            hours: hoursSum,
            from: periods[0]?.startsOn ?? null,
            to: periods.at(-1)?.endsOn ?? null,
            span: periods.length,
        };
    }
    const total = tally(input, 'all');
    const months = scenarioMonths(input);
    const margin = total.revenue - total.labour - external;
    return {
        revenue: total.revenue,
        labour: total.labour,
        external,
        margin,
        marginPct: total.revenue > 0 ? margin / total.revenue : null,
        hours: total.hours,
        from: months[0] ?? null,
        to: months.at(-1) ?? null,
        span: months.length,
    };
}
/**
 * DS-3 · the Mondays whose week starts within a month — where a month's hours land in the plan's
 * weekly grid, evenly. A month always has four or five.
 */
function mondaysIn(month) {
    const out = [];
    const at = new Date(`${firstOf(month)}T00:00:00Z`);
    const target = at.getUTCMonth();
    while (at.getUTCDay() !== 1)
        at.setUTCDate(at.getUTCDate() + 1);
    while (at.getUTCMonth() === target) {
        out.push(at.toISOString().slice(0, 10));
        at.setUTCDate(at.getUTCDate() + 7);
    }
    return out;
}
//# sourceMappingURL=scenario.js.map